import queryString from 'query-string';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpWrapper } from '@/shared/httpWrapper';
import '@/global';
import { SchemaCompositionOptions, SchemaDTO, SimpleSchema } from '@/shared/helpers/api/types/schemaModel.ts';
import {
    Application,
    Attachment,
    AttributeDefinition,
    ChangedCodeWithID,
    Code,
    CodeSet,
    CodeWithId,
    CommonLibraryUser,
    DisabledFeature,
    Job,
    Library,
    LibraryAuditLogEntryDto,
    PublishFilter,
    Release,
    ReleaseSpecification,
    ReleaseState,
    ScopeCodeSetRelease,
    Subscription,
    CodeSetSubscriptions,
    SubscriptionStatus,
    LibrarySubscriptionsAndWebhooks,
    Tag,
    Webhook,
    PublishCodeSetFilter
} from '@/shared/helpers/api/types/entities.ts';
import { QuerySpecVersioned } from '@/shared/helpers/api/types/genericViews.ts';
import { CodeEditContextResponse } from '@/shared/helpers/api/types/codeEditContextResponse.ts';
import { AttributeValue } from '@/shared/codeedit/codeEditFields.ts';

// =============================================================================
// common

export type RequestPayload = unknown;

export type ResponsePromise<T> = Promise<AxiosResponse<T>>;

async function getResData(self: HttpWrapper, urlPath: string, options?: AxiosRequestConfig) {
    const url = constructInternalUrl(urlPath);
    return getCommand(self, url, options);
}

// These endpoints answer 204 No Content for an empty list; axios rejects every non-2xx status.
async function getResDataOrEmpty(self: HttpWrapper, urlPath: string) {
    const url = constructInternalUrl(urlPath);
    const res = await self.$http.get(url);
    return res.status === 204 ? [] : res.data;
}

async function postResData(self: HttpWrapper, urlPath: string, command?: RequestPayload) {
    return (await postCommand(self, urlPath, command)).data;
}

function constructInternalUrl(urlPath: string) {
    return `${window.config?.commonLibraryApiUrl}internal/${urlPath}`;
}

function constructExternalUrl(urlPath: string) {
    return `${window.config?.commonLibraryApiUrl}api/${urlPath}`;
}

async function getCommand(self: HttpWrapper, url: string, command?: AxiosRequestConfig) {
    if (!command)
        return (await self.$http.get(url)).data;
    else
        return (await self.$http.get(url, command)).data;
}

async function getFileCommand(self: HttpWrapper, partialUrl: string) {
    const url = constructExternalUrl(partialUrl);
    const options: AxiosRequestConfig = { responseType: 'blob' };
    return await self.$http.get(url, options);
}

async function getInternalFileCommand(self: HttpWrapper, partialUrl: string) {
    const url = constructInternalUrl(partialUrl);
    const options: AxiosRequestConfig = { responseType: 'blob' };
    return await self.$http.get(url, options);
}

async function patchCommand(self: HttpWrapper, urlPath: string, command?: RequestPayload) {
    const url = constructInternalUrl(urlPath);
    return await self.$http.patch(url, command);
}

async function putCommand(self: HttpWrapper, urlPath: string, command?: RequestPayload) {
    const url = constructInternalUrl(urlPath);
    return await self.$http.put(url, command);
}

async function postCommand(self: HttpWrapper, urlPath: string, command?: RequestPayload, options?: AxiosRequestConfig) {
    const url = constructInternalUrl(urlPath);
    return await self.$http.post(url, command, options);
}

async function deleteCommand(self: HttpWrapper, urlPath: string, command?: RequestPayload) {
    const url = constructInternalUrl(urlPath);
    return await self.$http.delete(url, { data: command, headers: { 'Content-Type': 'Application/json' } });
}

async function getResDataPub(self: HttpWrapper, urlPath: string, options?: AxiosRequestConfig) {
    const url = constructExternalUrl(urlPath);
    return getCommand(self, url, options);
}

async function postCommandPub(self: HttpWrapper, urlPath: string, command?: RequestPayload, options?: AxiosRequestConfig) {
    const url = constructExternalUrl(urlPath);
    return await self.$http.post(url, command, options);
}

export type NameValuePair = {
    name: string;
    value: string | number | boolean;
};

function requestParameters(ps: NameValuePair[]) {
    if (ps && ps.length > 0) {
        return '?' + ps.map(x => `${x.name}=${encodeURIComponent(x.value)}`).join('&');
    }
    return '';
}

// =============================================================================
// AccessGroup

export type AccessGroup = {
    id: number;
    name: string;
    policy: string;
};

export type ScopeIdWithType = {
    id: number;
    type: string;
};

export type AccessGroupWithData = AccessGroup & {
    libraries: string[];
    scopes: ScopeIdWithType[];
};

export type AccessGroupCreateModel = {
    name: string;
    policy: string;
};

export function getAccessGroups(self: HttpWrapper): Promise<AccessGroup[]> {
    return getResData(self, 'AccessGroup');
}
export function getAccessGroup(self: HttpWrapper, accessGroupId: number): Promise<AccessGroupWithData> {
    return getResData(self, `AccessGroup/${accessGroupId}`);
}
export function getAccessGroupForLibrary(self: HttpWrapper, libraryName: string): Promise<AccessGroup[]> {
    return getResData(self, `AccessGroup/ByLibraryName/${encodeURIComponent(libraryName)}`);
}
export function postAccessGroups(self: HttpWrapper, accessGroup: AccessGroupCreateModel): ResponsePromise<{ id: number }> {
    return postCommand(self, 'AccessGroup', accessGroup);
}
export function putAccessGroups(self: HttpWrapper, accessGroup: AccessGroup): ResponsePromise<void> {
    return putCommand(self, 'AccessGroup', accessGroup);
}
export function deleteAccessGroups(self: HttpWrapper, accessGroup: AccessGroup): ResponsePromise<void> {
    return deleteCommand(self, 'AccessGroup', accessGroup);
}
export function postAccessGroupLibrary(self: HttpWrapper, accessGroupName: string, library: string): ResponsePromise<void> {
    return postCommand(self, 'AccessGroup/Library', { accessGroupName: accessGroupName, libraryName: library });
}
export function deleteAccessGroupLibrary(self: HttpWrapper, accessGroupName: string, library: string): ResponsePromise<void> {
    return deleteCommand(self, 'AccessGroup/Library', { accessGroupName: accessGroupName, libraryName: library });
}

// =============================================================================
// CodeSet

export type CodeSetCreateModel = {
    name?: string;
    description?: string;
    definition?: string | null;
    isValid?: boolean;
    isReleaseReady?: boolean;
    codeNameRegex?: string | null;
    codeNameRegexErrorMessage?: string | null;
    lockedForDelete?: boolean | null;
};

export function updateCodeSet(self: HttpWrapper, libraryName: string, codeSetName: string, data: CodeSetCreateModel): ResponsePromise<void> {
    return patchCommand(self, `Libraries/${encodeURIComponent(libraryName)}/codesets/${encodeURIComponent(codeSetName)}`, { ...data, library: libraryName });
}
export function newCodeSet(self: HttpWrapper, libraryName: string, description: string): ResponsePromise<{ libraryName: string; codeSetName: string }> {
    return postCommand(self, `Libraries/${encodeURIComponent(libraryName)}/codesets`, { library: libraryName, description, isValid: true });
}
export function getCodeSets(self: HttpWrapper, library: string, scope = ''): Promise<CodeSet[]> {
    return getResData(self, `Libraries/${encodeURIComponent(library)}/codesets`, { params: { scope } });
}
export function getCodeSetsByLibraries(self: HttpWrapper, libraries: string[], scope = ''): ResponsePromise<CodeSet[]> {
    return postCommand(self, 'Libraries/codesets', libraries, { params: { scope } });
}
export function getCodeSet(self: HttpWrapper, library: string, codeSetName: string): Promise<CodeSet> {
    return getResData(self, `Libraries/${encodeURIComponent(library)}/codesets/${encodeURIComponent(codeSetName)}`);
}
export function getCodeSetNameAndCodeIdentity(self: HttpWrapper, libraryName: string, scope: string, codeName: string): Promise<{ codeSetName: string; codeIdentity?: string }> {
    return getResData(self, `Libraries/${encodeURIComponent(libraryName)}/${encodeURIComponent(scope)}/${encodeURIComponent(codeName)}`);
}
export function createScopeCodeSetRelease(self: HttpWrapper, command: ScopeCodeSetRelease): ResponsePromise<Release> {
    return postCommand(self, 'Release/CreateScopeCodeSetRelease', command);
}

// =============================================================================
// CodeSetView

export type CodeSetViewResultCodeItem = {
    value: string;
    name?: string;
    description?: string;
    codeId?: string;
};

export type CodeSetViewResultCodeRow = {
    id: string;
    codeItems: CodeSetViewResultCodeItem[];
};

export type CodeSetViewResultCodeSet = {
    codeRow: CodeSetViewResultCodeRow[];
};

export type CodeSetViewResultData = {
    node: CodeSetViewResultCodeSet;
};

export type CodeSetViewResult = {
    data: CodeSetViewResultData;
};

export function getCodeSetViewOrderBy(self: HttpWrapper, codeSet: string, orderBy: string): Promise<CodeSetViewResult> {
    return getResData(self, `CodeSetView/codeset/${encodeURIComponent(codeSet)}`, { params: { orderBy } });
}

// =============================================================================
// CodeTables

export async function getCodeTables(self: HttpWrapper, scope: string[], libraries: string[], getValidOnly: boolean): Promise<Blob> {
    const requestOptions: AxiosRequestConfig = {
        params: {
            scope: scope,
            libraries: libraries,
            getValidOnly: getValidOnly
        },
        paramsSerializer(params) {
            return queryString.stringify(params, { arrayFormat: 'none' });
        },
        responseType: 'blob'
    };
    const responseData = await getResData(self, 'Excel/CodeTables', requestOptions);
    return new Blob([responseData], {
        type: responseData.type
    });
}

// =============================================================================
// Libraries

/**
 * A request for CodeEditContext. Needs to have at least one of codeId, codeSetId, and codeSetName.
 * See also /docs/codeedit.md.
 */
export type CodeEditContextRequest = {
    codeId?: number;
    codeSetId?: number;
    codeSetName?: string;
    codeRefsNotToLoad?: string[];
    /** Return every code for statically filtered references, marking the excluded ones with `isFiltered`. */
    includeFilteredReferences?: boolean;
};

export type CodeEditContextRefCode = {
    identity: string;
    id: number;
    name: string;
    description: string | null;
    isValid: boolean;
    isFiltered: boolean;
    dynamicFilterReferenceValues?: Record<string, string | number | boolean | null>;
};

export type CodeEditContext = {
    library: Library;
    code: Code;
    codeSet: string;
    referableCodes: Record<string, CodeEditContextRefCode[]>;
    codeNameRegex: string;
    codeNameRegexErrorMessage: string;
    codeDescriptionRegex: string;
    codeDescriptionRegexErrorMessage: string;
};

export type AttributeDefinitionDeleteModel = {
    attributeDefinitions: string[];
};

export type UpdateAttributeDefinitionsModel = {
    definitions: AttributeDefinition[];
    // Map of previous name => new name for renamed attributes.
    renames: Record<string, string>;
};

export type LibraryUpdateModel = {
    description?: string;
    definition?: string;
    alias?: string;
    specializedUI?: string;
    isValid?: boolean;
    isCaseSensitive?: boolean;
    isReadOnly?: boolean;
    areNamesUpperCase?: boolean;
    nameInIdentity?: boolean;
    nameMayChange?: boolean;
    nameMustBeUnique?: boolean;
    descriptionIsRequired?: boolean;
    isGlobal?: boolean;
    isScopeSpecific?: boolean;
    scopeType?: string;
    codeNameRegex?: string;
    codeNameRegexErrorMessage?: string;
    descriptionRegex?: string;
    descriptionRegexErrorMessage?: string;
    isRelation?: boolean;
};

export type RelatedLibrariesLibraryAttributes = {
    id: number;
    name: string;
    attributes: string[];
};

export type RelatedLibrariesRelation = {
    fromId: number;
    fromName: string;
    toId: number;
    toName: string;
    label: string;
};

export type RelatedLibrariesDTO = {
    relations: RelatedLibrariesRelation[];
    libraries: RelatedLibrariesLibraryAttributes[];
};

export type LibraryListItemPolicy = {
    role: string;
    system: string;
};

export type LibraryListItem = {
    id: number;
    name: string;
    description: string;
    alias: string;
    isGlobal: boolean;
    scopeType: string;
    isCaseSensitive: boolean;
    isVirtual: boolean;
    isReadOnly: boolean;
    isRelation: boolean;
    areNamesUpperCase: boolean;
    attachmentKey?: string;
    tags: string[];
    accessGroups: string[];
    policies: LibraryListItemPolicy[];
};

export type LibraryStatisticsReadModel = {
    libraryName: string;
    isGlobal: boolean;
    allIsReleaseReady: boolean;
    allLockedForDelete: boolean;
    minStatisticsUpdatedDate: string;
    maxStatisticsUpdatedDate: string;
    codeSetCount: number;
    codeCount: number;
    validCodeCount: number;
    stidCodeCount: number;
    stidValidCodeCount: number;
    stidMissingInStidCodeCount: number;
    stidMissingInCommonLibCodeCount: number;
    stidInBothButDifferentCodeCount: number;
    minStidSyncDate: string | null;
    maxStidSyncDate: string | null;
};

export type CodeSetStatisticsReadModel = {
    codeSetName: string;
    codeSetDescription: string;
    lockedForDelete: boolean;
    isReleaseReady: boolean;
    statisticsUpdatedDate: string;
    codeCount: number;
    validCodeCount: number;
    stidCodeCount: number;
    stidValidCodeCount: number;
    stidMissingInStidCodeCount: number;
    stidMissingInCommonLibCodeCount: number;
    stidInBothButDifferentCodeCount: number;
    stidSyncDate: string | null;
};

export type VirtualLibraryOptionsModel = {
    exists: boolean;
    scopeColumn: string | null;
    sourceQuery: string | null;
    compilerError: string | null;
    compilerWarning: string | null;
    dateUpdated: string;
};

export type VirtualLibraryOptionsValidationResult = {
    compilerError: string | null;
    compilerWarning: string | null;
    compilerNotifications: string[];
    dependsOnLibraries: string[];
};

export type RelationLibraryCreateModel = {
    name: string;
    description: string;
    isGlobal: boolean;
    scopeType?: string;
    referenceLibraries: ReferenceLibraryConfig[];
};

export type ReferenceLibraryConfig = {
    libraryName: string;
    codeRefName: string;
    treatAsAttribute: boolean;
    treatAsAttributeLabel: string | null;
};

function encodeURIComponentAllowNull(value: string | number | boolean | null) {
    // From MDN: "Other values are converted to strings."
    return encodeURIComponent(value as string);
}

export function fetchCodeEditContext(self: HttpWrapper, request: CodeEditContextRequest): ResponsePromise<CodeEditContextResponse> {
    return postCommand(self, 'Libraries/codeedit', request);
}

export function getLibraries(
    self: HttpWrapper,
    scope = '',
    tag = '',
    includeAttributeDefintions = false,
    includeCodeSets = false,
    includeAttachment = false,
    isGlobal: boolean | null = null
): Promise<Library[]> {
    return getResData(self, `Libraries?scope=${encodeURIComponent(scope)}&tag=${encodeURIComponent(tag)}&includeAttributeDefintions=${encodeURIComponent(includeAttributeDefintions)}&includeCodeSets=${encodeURIComponent(includeCodeSets)}&includeAttachment=${encodeURIComponent(includeAttachment)}&isGlobal=${encodeURIComponentAllowNull(isGlobal)}`);
}

export function getLibrary(
    self: HttpWrapper,
    library: string,
    { includeAccessGroups = true, includeAttributeDefintions = false, includeAttachment = true, includeTags = true, includeCodeSets = true } = {}
): Promise<Library> {
    return getResData(self, `Libraries/${encodeURIComponent(library)}?includeAccessGroups=${includeAccessGroups}&includeAttributeDefintions=${includeAttributeDefintions}&includeAttachment=${includeAttachment}&includeTags=${includeTags}&includeCodeSets=${includeCodeSets}`);
}

export function newLibrary(self: HttpWrapper, library: string): ResponsePromise<{ name: string }> {
    return postCommand(self, 'Libraries', library);
}

export function newRelationLibrary(self: HttpWrapper, relationLibrary: RelationLibraryCreateModel): ResponsePromise<{ name: string }> {
    return postCommand(self, 'Libraries/relation', relationLibrary);
}
export function getLibraryFromCodeSet(self: HttpWrapper, codeSet: string): Promise<CodeSet> {
    return getResData(self, 'CodeSet', { params: { name: codeSet } });
}
export function getAttributeDefinitions(self: HttpWrapper, library: string): Promise<AttributeDefinition[]> {
    return getResData(self, `Libraries/${encodeURIComponent(library)}/attributedefinitions`);
}
export function putAttributeDefinitions(self: HttpWrapper, library: string, definitions: AttributeDefinition[], renames: Record<string, string> = {}): ResponsePromise<void> {
    const model: UpdateAttributeDefinitionsModel = { definitions, renames };
    return putCommand(self, `Libraries/${encodeURIComponent(library)}/attributedefinitions`, model);
}
export function deleteAttributeDefinitions(self: HttpWrapper, library: string, definitions: AttributeDefinitionDeleteModel): ResponsePromise<void> {
    return deleteCommand(self, `Libraries/${encodeURIComponent(library)}/attributedefinitions`, definitions);
}
export async function getCode(self: HttpWrapper, codeset: string, code: string): Promise<CodeWithId> {
    return await getResData(self, `Code?codeSet=${encodeURIComponent(codeset)}&code=${encodeURIComponent(code)}`);
}
export function patchLibrary(self: HttpWrapper, library: string, command: LibraryUpdateModel): ResponsePromise<void> {
    return patchCommand(self, `Libraries/${encodeURIComponent(library)}`, command);
}
export function getLibraryWithScopes(self: HttpWrapper, library: string): Promise<Library> {
    return getResData(self, `Library?name=${encodeURIComponent(library)}`);
}
export function getLibrarySubscriptionsAndWebhooks(self: HttpWrapper, library: string): Promise<LibrarySubscriptionsAndWebhooks> {
    return getResData(self, `Libraries/subscriptions?libraryName=${encodeURIComponent(library)}`);
}
export function getLibraryRelatedLibraries(self: HttpWrapper, library: string): Promise<RelatedLibrariesDTO> {
    return getResData(self, `Libraries/related-libraries/${encodeURIComponent(library)}`);
}
export function getSpecializedUIs(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Libraries/SpecializedUI');
}
export function getLibraryList(self: HttpWrapper): Promise<LibraryListItem[]> {
    return getResData(self, 'LibraryList');
}
export function getLibraryTreeMap(self: HttpWrapper): Promise<string> {
    return getResData(self, 'Library/Treemap');
}
export function getScopesForLibraries(self: HttpWrapper, libraries: string[]): ResponsePromise<{ [libraryName: string]: string[] }> {
    return postCommand(self, `Libraries/GetScopesForLibraries`, libraries);
}
export function getLibrariesStatistics(self: HttpWrapper): Promise<LibraryStatisticsReadModel[]> {
    return getResData(self, 'Libraries/Statistics');
}
export function getLibrariesStatisticsWithScopeFilter(self: HttpWrapper, scopeName: string): Promise<LibraryStatisticsReadModel[]> {
    return getResData(self, `Libraries/StatisticsWithScopeFilter/${encodeURIComponent(scopeName)}`);
}
export function getLibraryStatisticDetails(self: HttpWrapper, libraryName: string, includeStidDiffOnly: boolean): Promise<CodeSetStatisticsReadModel[]> {
    return getResData(self, `Libraries/Statistics/${encodeURIComponent(libraryName)}?includeStidDiffOnly=${includeStidDiffOnly}`);
}
export function recomputeLibraryStatistics(self: HttpWrapper, libraryName: string): ResponsePromise<void> {
    return putCommand(self, `Libraries/RecomputeStatistics/${encodeURIComponent(libraryName)}`);
}
export function getVirtualLibraryDependencies(self: HttpWrapper, libraryName: string): Promise<string[]> {
    return getResData(self, `Libraries/VirtualLibraryDependencies/${encodeURIComponent(libraryName)}`);
}
export function getVirtualLibraryOptions(self: HttpWrapper, libraryName: string): Promise<VirtualLibraryOptionsModel> {
    return getResData(self, `Libraries/VirtualLibraryOptions/${encodeURIComponent(libraryName)}`);
}
export function validateVirtualLibraryOptions(self: HttpWrapper, model: VirtualLibraryOptionsModel): ResponsePromise<VirtualLibraryOptionsValidationResult> {
    return postCommand(self, `Libraries/VirtualLibraryOptions`, model);
}
export function postVirtualLibraryOptions(self: HttpWrapper, libraryName: string, model: VirtualLibraryOptionsModel): ResponsePromise<void> {
    return postCommand(self, `Libraries/VirtualLibraryOptions/${encodeURIComponent(libraryName)}`, model);
}
export type ReferenceFilterValidationResult = {
    isValid: boolean;
    errorMessage: string | null;
    isEmptyResult: boolean;
    matchCount: number;
};
export function validateReferenceFilter(self: HttpWrapper, referenceLibraryName: string, filter: string): ResponsePromise<ReferenceFilterValidationResult> {
    return postCommand(self, `Libraries/AttributeDefinitions/ValidateReferenceFilter`, { referenceLibraryName, filter });
}

export function getLibraryAuditLog(self: HttpWrapper, libraryName: string): Promise<LibraryAuditLogEntryDto[]> {
    return getResData(self, `LibraryAuditLog/Library/${encodeURIComponent(libraryName)}`);
}
export function getLibrariesAuditLog(self: HttpWrapper): Promise<LibraryAuditLogEntryDto[]> {
    return getResData(self, `LibraryAuditLog`);
}
// =============================================================================
// Code

export type ReferableCode = {
    codeId: number;
    codeName: string;
    codeDescription: string;
    codeSetName: string;
    codeSetDescription: string;
    libraryName: string;
};

export type FindReferringCodesResult = {
    totalCount: number;
    codes: ReferableCode[];
};

export type CodeAttributeUpdateInfo = {
    createdBy: CommonLibraryUser | null;
    updatedBy: CommonLibraryUser | null;
    dateCreated: string | null;
    dateUpdated: string | null;
    attributeName: string | null;
};

export type CodeUpdateInfo = {
    createdBy: CommonLibraryUser | null;
    updatedBy: CommonLibraryUser | null;
    dateCreated: string | null;
    dateUpdated: string | null;
    codeId: number;
    attributes: CodeAttributeUpdateInfo[];
};

export type CodeHistoryItem = {
    releaseId: number;
    dateUpdated: string | null;
    createdBy: CommonLibraryUser;
    releaseChanges: ChangedCodeWithID;
};

export type CodeHistory = {
    codeId: number;
    releasesWithChanges: CodeHistoryItem[];
};

export type RelatedCodeItem = {
    id: number;
    name: string;
    description: string | null;
    codeSetName: string;
    identity: string;
};

export type RelatedCodeGroup = {
    label: string | null;
    codes: RelatedCodeItem[];
    referenceDisplayMode: number;
    description: string | null;
};

export function getReferingCodes(self: HttpWrapper, codeSet: string, id: number, libName: string = ''): Promise<FindReferringCodesResult> {
    return getResData(self, 'Code/ReferringCodes', {
        params: {
            codeSet,
            code: id,
            withLibrary: libName
        }
    });
}

export function getRelatedCodes(self: HttpWrapper, codeSet: string, identity: string): Promise<RelatedCodeGroup[]> {
    return getResData(self, `Code/RelatedCodes`, {
        params: {
            codeSet: codeSet,
            code: identity
        }
    });
}
export function getReferringCodesForInvalidCodes(self: HttpWrapper, codeSet: string, libName: string = ''): Promise<ReferableCode[]> {
    return getResData(self, 'Code/ReferringCodesForInvalidCodes', { params: { codeSet, withLibrary: libName } });
}
export function getIdentity(self: HttpWrapper, codeSetName: string, codeAttributes: Record<string, string | number | boolean | null | undefined>): ResponsePromise<string> {
    return postCommand(self, 'Code/Identity', { codeSetName, codeAttributes });
}

export type CodeSearchHit = {
    id: number;
    identity: string;
    name: string;
    description: string | null;
    isValid: boolean;
};

export type CodeSearchRequest = {
    codeSetName: string;
    /** `*` or `%` match any characters. */
    name?: string;
    /** Identity attribute values by attribute name; an empty value matches any value. */
    attributes?: Record<string, string>;
    take?: number;
};

export type CodeSearchResponse = {
    codes: CodeSearchHit[];
    hasMore: boolean;
    /** The identity a new item would get; only set when the search has no wildcard and identifies one item. */
    exactIdentity: string | null;
    exactMatch: CodeSearchHit | null;
};

export type NewCodeValidationResponse = {
    /** The identity the new item would get. */
    identity: string;
    /** Why no new item can be created from the values, in the words saving uses; empty when it can. */
    errors: string[];
};

export function searchCodes(self: HttpWrapper, request: CodeSearchRequest): ResponsePromise<CodeSearchResponse> {
    return postCommand(self, 'Code/Search', request);
}

/** Runs the validation saving uses, which reads the whole code set, so only call it when the user is about to create. */
export function validateNewCode(self: HttpWrapper, request: CodeSearchRequest): ResponsePromise<NewCodeValidationResponse> {
    return postCommand(self, 'Code/ValidateNew', request);
}
export function getCodeById(self: HttpWrapper, codeId: number): Promise<Code> {
    return getResData(self, `Code/${encodeURIComponent(codeId)}`);
}
export function getCodeUpdateInfo(self: HttpWrapper, codeId: number): Promise<CodeUpdateInfo> {
    return getResData(self, `Code/UpdateInfo?code=${codeId}`);
}
export function getCodeHistory(self: HttpWrapper, codeId: number): Promise<CodeHistory> {
    return getResData(self, `Code/History?codeId=${codeId}`);
}
export function getCodeByIdWithReferences(self: HttpWrapper, library: string, codeId: number): ResponsePromise<Record<string, unknown>[]> {
    return getResData(self, `Code/${encodeURIComponent(library)}/${codeId}/WithReferences`);
}

// =============================================================================
// Logs

export type ChangeReceiverResult = {
    success: boolean;
    description: string;
    message: string;
    scopeName: string;
    codeResults: {
        codeName: string;
        codeIdentity: string;
        success: boolean;
        message: string;
    }[];
};

export type ReleaseLogStringData = {
    data: string;
};

export type CodeDeletedLogData = {
    iri: string | null;
};

export type ReleaseLogData = | ReleaseLogStringData
    | ChangeReceiverResult
    | CodeDeletedLogData;

export type LogLevel = 'Info' | 'Warning' | 'Error';

export type ReleaseLog = {
    id: number;
    timestamp: string;
    codeSetName: string;
    codeSetDescription: string;
    libraryName: string;
    releaseId: number | null;
    publishJobId: number | null;
    message: string;
    data: ReleaseLogData;
    logLevel: LogLevel;
};

export function getLogs(self: HttpWrapper, filter: AxiosRequestConfig): Promise<ReleaseLog[]> {
    return getResData(self, 'Log/Get', filter);
}

// =============================================================================
// Menu

export type MenuItem = {
    id: number;
    name: string;
    description: string;
    parentId: number;
    childItems: MenuItem[];
    path: string;
    isHighlighted: boolean;
};

export function getDynamicMenuItems(self: HttpWrapper): Promise<MenuItem[]> {
    return getResData(self, 'Menu');
}

// =============================================================================
// Scopes
export type ScopeWithDescription = {
    identity: string;
    description: string;
};

export function getScopeTypes(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Scopes');
}
export function getUsedScopeTypes(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Scopes/used');
}
export function getScopes(self: HttpWrapper, scopeType: string): Promise<ScopeWithDescription[]> {
    return getResData(self, 'Scopes/' + encodeURIComponent(scopeType));
}

export type ScopeRegEx = {
    /** Name of the Scope */
    name: string;
    /** Regex for validating the scope */
    regEx: string;
};

export type LsdLibraryResult = {
    Id: number;
    Library: string;
    Alias: string;
    Groups: string[];
    ScopeCounts: Record<string, number>;
    Total: number;
    IsGrouped: boolean | null;
    CodeSetIsReleaseReady: Record<string, boolean>;
};

export type LsdResult = {
    Scopes: string[];
    Groups: Record<string, boolean>;
    Libraries: LsdLibraryResult[];
};

/**
 * Fetches Regex for scopeType, returns a ScopeRegEx object when found and an empty string if not found.
 */
export function getScopesTypeRegex(self: HttpWrapper, scopeType: string): Promise<ScopeRegEx | string> {
    return getResData(self, `Scopes/${encodeURIComponent(scopeType)}/RegEx`);
}
export function getScopesByScopeTypes(self: HttpWrapper, scopeTypes: string[]): ResponsePromise<string[]> {
    return postCommand(self, 'Scopes/getScopes', scopeTypes);
}
export function getScopeTypeLibraryReport(self: HttpWrapper, scopeType: string): Promise<LsdResult> {
    return getResData(self, `Scopes/${encodeURIComponent(scopeType)}/library-report`);
}

// =============================================================================
// Tag

export function getTagUniqueKeys(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Tag/Keys/Unique');
}
export function getTagMetadata(self: HttpWrapper, tag: string): Promise<Record<string, string>> {
    return getResData(self, `Tag/${encodeURIComponent(tag)}`);
}
export function getAllTags(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Tag');
}
export function getAllTagsAsEntities(self: HttpWrapper): Promise<Tag[]> {
    return getResData(self, 'Tag/Entities');
}
export function getAttachmentForTag(self: HttpWrapper, tagName: string): Promise<Attachment> {
    return getResData(self, `Tag/Attachment/${encodeURIComponent(tagName)}`);
}
export function deleteTagMetadataElement(self: HttpWrapper, tag: string, element: string): ResponsePromise<void> {
    return deleteCommand(self, `Tag/${encodeURIComponent(tag)}/${encodeURIComponent(element)}`);
}
export function saveTagMetadata(self: HttpWrapper, tag: string, elements: string): ResponsePromise<void> {
    return postCommand(self, `Tag/${encodeURIComponent(tag)}`, elements);
}
export function tagLibrary(self: HttpWrapper, tag: string, library: string): ResponsePromise<void> {
    return postCommand(self, 'LibraryUpdate/Tag', { libraryName: library, tagName: tag });
}
export function untagLibrary(self: HttpWrapper, tag: string, library: string): ResponsePromise<void> {
    return postCommand(self, 'LibraryUpdate/Untag', { libraryName: library, tagName: tag });
}
export function updateTag(self: HttpWrapper, tag: string): ResponsePromise<void> {
    return putCommand(self, 'Tag/Entity', tag);
}

// =============================================================================
// Me

export function getMyUserName(self: HttpWrapper): Promise<string> {
    return getResData(self, 'Me');
}
/**
 * Fetches users permissions, returns an empty [] if user don't have any permissions.
 */
export function getMyPermissions(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Me/permissions');
}
/**
 * Clears the Cache for the users attributes on the API. Returns only a HTTP 200 response if successful.
 */
export function clearAttributesApiCache(self: HttpWrapper): Promise<void> {
    return getResData(self, 'Me/clearcache');
}

export function validateAccessCanEditLibrary(self: HttpWrapper, library: string): Promise<boolean> {
    return getResData(self, `Me/ValidateAccess/CanEditLibrary/${encodeURIComponent(library)}`);
}

export type UserLibraryAccessCodeEdit = {
    grantedByRole: string;
    scopes: { name: string; grantedByExternalSystem: boolean }[];
};

export type UserLibraryAccessLibraryEdit = {
    grantedByRole: string;
};

export type UserLibraryAccess = {
    libraryName: string;
    isGlobalLibrary: boolean;
    libraryEdit: UserLibraryAccessLibraryEdit | null;
    codeEdit: UserLibraryAccessCodeEdit | null;
};

/**
 * Fetches the current user's library access.
 */
export function getMyLibraryAccess(self: HttpWrapper): Promise<UserLibraryAccess[]> {
    return getResData(self, 'Me/LibraryAccess');
}

export function validateAccessCanEditCode(self: HttpWrapper, library: string, scopes: string[]): Promise<boolean> {
    return getResData(self, `Me/ValidateAccess/CanEditCode/${encodeURIComponent(library)}`, {
        params: { scopes },
        paramsSerializer(params) {
            return queryString.stringify(params, { arrayFormat: 'none' });
        }
    });
}
export function validateAccessCanEditCodeForAllScopes(self: HttpWrapper, library: string): Promise<Record<string, boolean>> {
    return getResData(self, `Me/ValidateAccess/CanEditCode/${encodeURIComponent(library)}/AllScopes`);
}
export function validateAccessCanEditTagByName(self: HttpWrapper, tagName: string): Promise<boolean> {
    return getResData(self, `Me/ValidateAccess/CanEditTag/Name/${encodeURIComponent(tagName)}`);
}
export function validateAccessCanTransitionRelease(self: HttpWrapper, releaseId: number): Promise<boolean> {
    return getResData(self, `Me/ValidateAccess/CanTransitionRelease/${releaseId}`);
}

// =============================================================================
// Releases

enum OrderByDirection {
    Ascending = 'Ascending',
    Decending = 'Decending' // Known typo in the API
}

export type PagingParameters = {
    take?: number;
    skip?: number;
    direction?: OrderByDirection;
};

export type ReleaseQueryFilter = {
    pagingParameters?: PagingParameters;
    states?: ReleaseState[];
    userId?: string;
};

/**
 * The specification types a release can have, as reported by the
 * SpecificationType computed column on the server.
 */
export type ReleaseSpecificationType = ReleaseSpecification['specificationType'];

export type ReleaseSearchFilter = {
    states?: ReleaseState[];
    specificationTypes?: ReleaseSpecificationType[];
    libraries?: string[];
    scopes?: string[];
    /** ISO date, inclusive. */
    createdFrom?: string | null;
    /** ISO date, inclusive. */
    createdTo?: string | null;
    onlyMine?: boolean;
    createdByContains?: string | null;
    take?: number;
};

/**
 * Slim projection of a release, holding only what the search result grid needs.
 * Deliberately excludes the specification document, jobs and code sets.
 */
export type ReleaseSearchResult = {
    id: number;
    state: ReleaseState;
    specificationType: ReleaseSpecificationType | null;
    dateCreated: string;
    dateUpdated: string | null;
    createdByName: string | null;
    createdByEmail: string | null;
    /** True when the specification recorded errors; the messages are not included. */
    hasErrors: boolean;
    libraries: string[];
    scopes: string[];
    releaseChainId: number | null;
};

export type ReleaseSearchResults = {
    releases: ReleaseSearchResult[];
    /** The row limit actually applied, after the server clamped it. */
    take: number;
    /** True when the row limit was reached, so more releases may match. */
    isTruncated: boolean;
};

/** A library that has at least one release, and the groups it belongs to. */
export type ReleaseSearchLibrary = {
    name: string;
    groups: string[];
};

export type PublishedReleaseForLibrary = {
    id: number;
    dateCreated: string;
    codeSets: { name: string; description: string | null }[];
};

type BatchDocumentValue = string | number | boolean | null;

export type BatchDocumentCode = Record<string, BatchDocumentValue>;

export type BatchDocument = {
    codeSetName: string;
    codes: BatchDocumentCode[];
};

export type PublishCountRequest = {
    applicationName: string;
    libraries: string[];
    facilities: string[];
    isValid: boolean | null;
    updatedSince: string | null;
};

export type ReleaseAutoSubmitResult = {
    releaseId: number;
    message: string;
    wasAutoSubmit?: boolean;
};

export type PublishCountResponse = {
    count: number;
    libraries: {
        name: string;
        count: number;
        facilities: {
            name: string;
            count: number;
        }[];
    }[];
};

export type CheckIdentityResult
    = | { conflict: true; message: string; identity: string; id: number; isValid: boolean }
        | { conflict: false; identity: string };

/**
 * Searches releases, returning the newest matching ones up to the row limit.
 * All filtering happens on the server: because the result is capped, filtering
 * on the client would only ever see an already truncated window.
 */
export function searchReleases(self: HttpWrapper, filter: ReleaseSearchFilter): Promise<ReleaseSearchResults> {
    const query = new URLSearchParams();
    filter.states?.forEach(state => query.append('states', state));
    filter.specificationTypes?.forEach(type => query.append('specificationTypes', type));
    filter.libraries?.forEach(library => query.append('libraries', library));
    filter.scopes?.forEach(scope => query.append('scopes', scope));

    if (filter.createdFrom) query.append('createdFrom', filter.createdFrom);
    if (filter.createdTo) query.append('createdTo', filter.createdTo);
    if (filter.onlyMine) query.append('onlyMine', 'true');
    if (filter.createdByContains) query.append('createdByContains', filter.createdByContains);
    if (typeof filter.take === 'number') query.append('take', String(filter.take));

    return getResData(self, `Release/search?${query.toString()}`);
}

/** Libraries that have at least one release, with their groups. Cached server-side. */
export function getReleaseSearchLibraries(self: HttpWrapper): Promise<ReleaseSearchLibrary[]> {
    return getResData(self, 'Release/search/libraries');
}

/** Distinct scope names that have at least one release. Cached server-side. */
export function getReleaseSearchScopes(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Release/search/scopes');
}

export function getPublishedReleasesForLibrary(self: HttpWrapper, libraryName: string, take = 100): Promise<PublishedReleaseForLibrary[]> {
    return getResData(self, `Release/published/library/${encodeURIComponent(libraryName)}?take=${take}`);
}

export function getMyReleases(self: HttpWrapper, states: string[] = []): Promise<Release[]> {
    return getResData(self, `Release/mine?states=${states.join('&states=')}`);
}
export function getRelease(self: HttpWrapper, releaseId: number): Promise<Release> {
    return getResData(self, `Release/${releaseId}`);
}
export function createChangeDoc(self: HttpWrapper, codeSetName: string, codes: BatchDocumentCode[]): ResponsePromise<ReleaseAutoSubmitResult> {
    return postCommand(self, 'Release/autosubmit', { codeSetName, codes });
}
export function createChangeDocAndCommit(self: HttpWrapper, codeSetName: string, codes: BatchDocumentCode[]): ResponsePromise<ReleaseAutoSubmitResult> {
    return postCommand(self, 'Release/CreateAndCommit', { codeSetName, codes });
}
export function createChangeDocAndCommitCodesets(self: HttpWrapper, collection: BatchDocument[]): ResponsePromise<ReleaseAutoSubmitResult> {
    return postCommand(self, 'Release/CreateAndCommitCodesets', collection);
}

export type CreateReleaseChainRequest = {
    codeSetName: string;
    libraryName: string;
    codes: BatchDocumentCode[];
}[];
export function createReleaseChain(self: HttpWrapper, collection: CreateReleaseChainRequest): ResponsePromise<ReleaseAutoSubmitResult> {
    return postCommand(self, 'Release/CreateReleaseChain', collection);
}
export function getReleaseChanges(self: HttpWrapper, releaseId: number): Promise<ChangedCodeWithID[]> {
    return getResData(self, `Release/${releaseId}/Changes`);
}
export function removeReleaseChanges(self: HttpWrapper, releaseId: number, changeIds: number[]): ResponsePromise<void> {
    return deleteCommand(self, `Release/${releaseId}/Changes`, changeIds);
}
export function transitionRelease(self: HttpWrapper, releaseId: number, state: string): ResponsePromise<void> {
    return postCommand(self, `Release/Transition/${state}/${releaseId}`);
}
export function createPublishingRelease(self: HttpWrapper, specification: PublishFilter): ResponsePromise<Release> {
    return postCommand(self, 'Release/CreatePublishingRelease', specification);
}

export function createCodeSetPublishingRelease(self: HttpWrapper, specification: PublishCodeSetFilter): ResponsePromise<Release> {
    return postCommand(self, 'Release/CreateCodeSetPublishingRelease', specification);
}
export function deleteCode(self: HttpWrapper, codeSetName: string, codeIdentity: string): ResponsePromise<void> {
    return deleteCommand(self, 'Release/code', { codeSetName: codeSetName, codeIdentity: codeIdentity });
}
export function copyCode(self: HttpWrapper, codeId: number, targetCodeSets: string[]): ResponsePromise<Release> {
    return postCommand(self, `Release/code/${codeId}/copy`, { targetCodeSets });
}
/** The most codes (codes × target library sets) one copy may create. Must match CopyCodeService.MaxCopyRows on the server. */
export const MAX_COPY_ROWS = 50_000;
export type CopyFilteredCodesRequest = {
    targetCodeSets: string[];
    regEx: string[];
    queries: string[][];
};
export function copyFilteredCodes(self: HttpWrapper, codeSetName: string, request: CopyFilteredCodesRequest): ResponsePromise<Release> {
    return postCommand(self, `Release/codeset/${encodeURIComponent(codeSetName)}/copy`, request);
}
export function getPublishCount(self: HttpWrapper, query: PublishCountRequest): ResponsePromise<PublishCountResponse> {
    return postCommand(self, 'Release/GetPublishCount', query);
}
export function checkIdentityConflict(self: HttpWrapper, codeSetName: string, code: Record<string, AttributeValue>): ResponsePromise<CheckIdentityResult> {
    return postCommand(self, 'Release/checkIdentityConflict', { codeSetName: codeSetName, attributeValues: code });
}

export type CheckUniquenessRequest = {
    codeId?: number;
    codeSetName: string;
    field: string;
    value: string;
};

export type CheckUniquenessResult = {
    field: string;
    hasDuplicates: boolean;
    duplicates: {
        id: number;
        value: string;
    }[];
};

export function checkUniquenessConflict(self: HttpWrapper, request: CheckUniquenessRequest): Promise<CheckUniquenessResult> {
    return postResData(self, 'Release/CheckUniquenessConflict', request);
}
export function abortPublishingRelease(self: HttpWrapper, releaseId: number): ResponsePromise<void> {
    return putCommand(self, `Release/${releaseId}/AbortPublishing`);
}
export function discardRelease(self: HttpWrapper, releaseId: number): ResponsePromise<void> {
    return transitionRelease(self, releaseId, 'Discarded');
}

// =============================================================================
// KPI

export type KpiDataCounts = {
    libraryCount: number;
    codeSetCount: number;
    codeCount: number;
    codeAttributeCount: number;
    tagCount: number;
    accessGroupCount: number;
    releaseCount: number;
    aliasCount: number;
    subscriptionCount: number;
    webhookCount: number;
    scheduleCount: number;
    userCount: number;
    usersLastWeek: number;
    usersLastMonth: number;
    usersLastYear: number;
    attachmentCount: number;
};

export type KpiResult = {
    labels: string[];
    series: Record<string, number[]>;
    sums: Record<string, number>;
};

export type ChartData = {
    labels: string[];
    datasets: Dataset[];
};

export type Dataset = {
    label: string;
    data: number[];
    backgroundColor?: string;
    stack?: string;
};

export function getKpiDataCounts(self: HttpWrapper): Promise<KpiDataCounts> {
    return getResData(self, 'KPI/DataCounts');
}
export function getKpiReleaseStats(self: HttpWrapper, days: number): Promise<KpiResult> {
    return getResData(self, 'KPI/ReleaseStats?days=' + days);
}
export function getKpiRequestsByDay(self: HttpWrapper): Promise<ChartData> {
    return getResData(self, 'KPI/RequestsByDay');
}

// =============================================================================
// Stats

export type RecentStatsResponse = {
    users: {
        userName: string;
        userPrincipalName?: string;
        userId?: string;
        clientName?: string;
        requestCount: number;
        resourceCount: number;
    }[];
    resources: {
        resourceName: string;
        resourceType: number;
        resourceCount: number;
        userCount: number;
    }[];
    usageByTime: ChartData;
};

export function getRecentStats(self: HttpWrapper, intervalMinutes: number, durationHours: number): Promise<RecentStatsResponse> {
    return getResData(self, `Stats/Recent?interval-minutes=${intervalMinutes}&duration-hours=${durationHours}`);
}

export type LibraryStatsResponse = {
    requestsByClient: ChartData;
    requestsByDay: ChartData;
    requestCountTotal: number;
    requestCountApplications: number;
    applicationsCount: number;
};

export function getLibraryStats(self: HttpWrapper, libraryName: string): Promise<LibraryStatsResponse> {
    return getResData(self, 'Stats/Library/' + encodeURIComponent(libraryName));
}

export type ClientApplicationStats = {
    userId: string;
    description: string;
    countToday: number;
    countThisMonth: number;
    countPrevMonth: number;
};

export function getClientApplications(self: HttpWrapper): Promise<ClientApplicationStats[]> {
    return getResData(self, 'Stats/ClientApplications');
}

export type ClientApplicationDetailsResponse = {
    application: {
        id: number;
        name: string;
        identity: string;
        description: string | null;
        remarks: string | null;
        objectId: string | null;
        homepage: string | null;
        accessGranted: boolean | null;
        contact1: string | null;
        contact1Mail: string | null;
        contact2: string | null;
        contact2Mail: string | null;
        applicationId: string | null;
        dateCreated: string | null;
        dateUpdated: string | null;
    } | null;
    topEndpoints: {
        endpoint: string | null;
        totalCount: number;
        failureCount: number;
    }[];
    topResources: {
        id: number;
        name: string;
        type: number;
        totalCount: number;
        failureCount: number;
    }[];
    dailyUsage: {
        year: number;
        month: number;
        day: number;
        totalCount: number;
    }[];
    monthlyUsage: {
        year: number;
        month: number;
        totalCount: number;
    }[];
    recentUsage: {
        time: string;
        totalCount: number;
    }[];
    savedQueries: {
        guid: string;
        name: string;
        totalCount: number;
    }[];
};

export function getClientApplicationDetails(self: HttpWrapper, userId: string): Promise<ClientApplicationDetailsResponse> {
    return getResData(self, `Stats/ClientApplications/${userId}`);
}

export type CorrelatedEventDetailsResponse = {
    correlationId: string;
    application: {
        name: string | null;
        identity: string | null;
        description: string | null;
        objectId: string | null;
        homepage: string | null;
        applicationId: string | null;
    } | null;
    events: {
        id: number;
        timestamp: string;
        endpoint: string | null;
        success: boolean;
        durationMilliseconds: number;
        scopeArgument: string | null;
        objectCount: number | null;
        resourceName: string | null;
        resourceType: number;
        messageValue: string | null;
    }[];
    savedQuery: {
        guid: string;
        name: string;
        spec: string | null;
    } | null;
};

export function getCorrelatedEventDetails(self: HttpWrapper, correlationId: string): Promise<CorrelatedEventDetailsResponse> {
    return getResData(self, `Stats/Event/Correlation/${correlationId}`);
}

// =============================================================================

export type Favourite = {
    id: number;
    userId: string;
    title: string;
    url: string;
    dateCreated: string;
};

export type FavouriteArguments = {
    title: string;
    url: string;
};

export function getFavourites(self: HttpWrapper): Promise<Favourite[]> {
    return getResData(self, 'favourite');
}
export function addFavourite(self: HttpWrapper, fav: FavouriteArguments): ResponsePromise<void> {
    return postCommand(self, 'favourite', fav);
}
export function updateFavourite(self: HttpWrapper, id: number, fav: FavouriteArguments): ResponsePromise<void> {
    return putCommand(self, `favourite/${id}`, fav);
}
export function deleteFavourite(self: HttpWrapper, id: number): ResponsePromise<void> {
    return deleteCommand(self, `favourite/${id}`);
}

// =============================================================================
// Reports

export function getReport(self: HttpWrapper, reportName: string, filter: string): Promise<unknown[]> {
    return getResDataPub(self, `Report/${encodeURIComponent(reportName)}`, { params: { $filter: filter } });
}

// =============================================================================
// WebHooks

export type WebhookSubscriptionModel = {
    callbackUrl: string;
    ownerId?: string;
    library: string;
    secret: string;
    enabled: boolean;
};

export function getWebHooks(self: HttpWrapper): Promise<Webhook[]> {
    return getResData(self, 'Webhooks');
}
export function addWebhook(self: HttpWrapper, hook: WebhookSubscriptionModel): ResponsePromise<{ id: number }> {
    return postCommand(self, 'Webhooks', hook);
}
export function deleteWebhook(self: HttpWrapper, id: number): ResponsePromise<void> {
    return deleteCommand(self, `Webhooks/${id}`);
}
export function enableWebHook(self: HttpWrapper, id: number): ResponsePromise<void> {
    return patchCommand(self, `Webhooks/${id}/enable`);
}
export function disableWebHook(self: HttpWrapper, id: number): ResponsePromise<void> {
    return patchCommand(self, `Webhooks/${id}/disable`);
}

// =============================================================================
// Monitoring

export type JobIssueDTO = {
    jobId: number;
    jobState: string;
    jobType: string;
    jobDateUpdated: string;
    retryCount: number;
    webhookId: number | null;
    codeSet: string;
    releaseId: number;
    releaseState: string;
    releaseUserName: string;
    releaseUserPrincipal: string;
    subscriptionId: number | null;
    application: string;
};

export type ScheduleDTO = {
    scheduleExecutionId: number;
    scheduleId: number;
    startDate: string;
    endDate: string | null;
    running: boolean;
    failed: boolean;
    action: string;
    description: string;
};

export type ScheduleDeltaTimeDTO = {
    scheduleId: number;
    action: string;
    description: string;
    elapsedSecondsAverage: number;
    elapsedSecondsLatest: number;
    delta: number;
};

export type ScheduleExecutionOutput = {
    output: string;
};

export type ReleaseDTO = {
    id: number;
    state: string;
    userName: string;
    userPrincipal: string;
    dateCreated: string;
    errorCount: number;
    specificationType: string;
    library: string;
    codeSetName: string;
    codeSetDescription: string;
};

export type LateSchedulesDTO = {
    id: number;
    action: string;
    description: string;
    scheduleSpec: string;
    latestDate: string;
    expectedDate: string;
};

export function adminPageJobs(self: HttpWrapper): Promise<JobIssueDTO[]> {
    return getResData(self, 'AdminPage/Jobs');
}
export function adminPageSchedules(self: HttpWrapper): Promise<ScheduleDTO[]> {
    return getResData(self, 'AdminPage/Schedules');
}
export function adminPageSchedulesSignificantDeltaTimeChange(self: HttpWrapper): Promise<ScheduleDeltaTimeDTO[]> {
    return getResData(self, 'AdminPage/SchedulesWithSignificantDeltaTimeChange');
}
export function adminPageScheduleOutput(self: HttpWrapper, id: number): Promise<ScheduleExecutionOutput> {
    return getResData(self, `AdminPage/Schedules/${id}`);
}
export function adminPageReleases(self: HttpWrapper): Promise<ReleaseDTO[]> {
    return getResData(self, 'AdminPage/Releases');
}
export function adminPageLateSchedules(self: HttpWrapper): Promise<LateSchedulesDTO[]> {
    return getResData(self, 'AdminPage/LateSchedules');
}
export function adminPageRestartJob(self: HttpWrapper, id: number): ResponsePromise<Job> {
    return postCommand(self, `AdminPage/RestartJob/${id}`);
}

export function adminPageArchiveJob(self: HttpWrapper, id: number): ResponsePromise<void> {
    return postCommand(self, `AdminPage/ArchiveJob/${id}`);
}
// =============================================================================
// Subscription

export type DifferenceCode = Record<string, unknown>;

export type DifferenceResult = {
    hasDifference: boolean;
    releaseNeeded: boolean;
    hasError: boolean;
    expectedCodeCount: number;
    externalCodeCount: number;
    externalValidCodeCount: number;
    error: string;
    notInExternal: DifferenceCode[];
    notInInternal: DifferenceCode[];
    inBothButDifferent: { item1: DifferenceCode; item2: DifferenceCode }[];
    summary: string;
};

export function getSubscription(self: HttpWrapper, id: number): Promise<Subscription> {
    return getResData(self, `Application/Subscription/${id}`);
}
export function getSubscriptionStatus(self: HttpWrapper, id: number): Promise<SubscriptionStatus[]> {
    return getResData(self, `Application/Subscription/${id}/Status`);
}
export function getSubscriptionStatusById(self: HttpWrapper, id: number): Promise<{ status: SubscriptionStatus; data: DifferenceResult }> {
    return getResData(self, `Application/Subscription/Status/${id}`);
}

// =============================================================================
// Alias

export type AliasDirection = 'Both' | 'Out' | 'In';

export type Alias = {
    id: number;
    key: string;
    value: string;
    scope: string;
    direction: AliasDirection;
    mapFunction: string;
};

export type AliasScope = {
    name: string;
};

export function getAliases(self: HttpWrapper): Promise<Alias[]> {
    return getResData(self, 'Alias');
}
export function getAliasScopes(self: HttpWrapper): Promise<AliasScope[]> {
    return getResData(self, 'Alias/Scope');
}
export function saveNewAlias(self: HttpWrapper, command: Alias): ResponsePromise<void> {
    return postCommand(self, 'Alias', command);
}
export function saveNewAliasScope(self: HttpWrapper, command: AliasScope): ResponsePromise<void> {
    return postCommand(self, 'Alias/Scope', command);
}
export function updateAlias(self: HttpWrapper, command: Alias): ResponsePromise<void> {
    return postCommand(self, 'Alias/Update', command);
}
export function deleteAlias(self: HttpWrapper, alias: Alias): ResponsePromise<void> {
    return deleteCommand(self, 'Alias', alias);
}

// =============================================================================
// Schedules

export type ScheduleState
    = | 'Ok'
        | 'Failed'
        | 'Running';

export type Schedule = {
    id: number;
    scheduleSpec: string;
    action: string;
    icons: string;
    category: string;
    description: string;
    configuration: string;
    enabled: boolean;
    lastKnownState: ScheduleState;
    lastExecutionTime: string;
};

export type ScheduleExecution = {
    id: number;
    scheduleId: number;
    startDate: string;
    endDate: string;
    running: boolean;
    failed: boolean;
    output: string;
};

export type ScheduleUpdateModel = {
    action: string;
    icons: string;
    category: string;
    description: string;
    configuration: string;
    scheduleSpec: string;
    enabled: boolean;
};

export function getSchedules(self: HttpWrapper): Promise<Schedule[]> {
    return getResData(self, 'Schedule');
}
export function getScheduleExecutions(self: HttpWrapper, id: number): Promise<ScheduleExecution[]> {
    return getResData(self, `Schedule/${id}/executions`);
}
export function updateSchedule(self: HttpWrapper, schedule: ScheduleUpdateModel): ResponsePromise<Schedule> {
    return putCommand(self, 'Schedule', schedule);
}
export function addSchedule(self: HttpWrapper, schedule: ScheduleUpdateModel): ResponsePromise<Schedule> {
    return postCommand(self, 'Schedule', schedule);
}
export function deleteSchedule(self: HttpWrapper, id: number): ResponsePromise<void> {
    return deleteCommand(self, `Schedule/${id}`);
}
export function deleteScheduleExecution(self: HttpWrapper, id: number): ResponsePromise<void> {
    return deleteCommand(self, `Schedule/executions/${id}`);
}
export function runSchedule(self: HttpWrapper, schedule: ScheduleUpdateModel): ResponsePromise<void> {
    return postCommand(self, 'Schedule/run', schedule);
}
export function runScheduleById(self: HttpWrapper, id: number): ResponsePromise<void> {
    return postCommand(self, `Schedule/run/${id}`);
}

// =============================================================================
// Saved Queries

export type SavedQuerySaveRequest = {
    name: string;
    spec: string;
    isPublic: boolean;
    isShared: boolean;
    queryType: number;
    pageSize?: number;
};

export type SavedQueryHeaderInfo = {
    id: number;
    guid: string;
    name: string;
    userId: string;
    userName: string;
    isPublic: boolean;
    isShared: boolean;
    queryType: number; // 0 => Original CL query format, 1 => SQL
};

export type SavedQuery = {
    id: number;
    guid: string;
    name: string;
    library: string;
    parameters: string;
    spec: string;
    userId: string;
    isValid: boolean;
    isPublic: boolean;
    isShared: boolean;
    isReport: boolean;
    dateCreated: string | null;
    dateUpdated: string | null;
    queryType: number; // 0 => Original CL query format, 1 => SQL
    pageSize: number | null;
    usage: string | null;
    usedBy: string | null;
    usedByExternalApplication: boolean;
};

export type SavedQueryUsageUpdateRequest = {
    usage: string;
    usedBy: string;
    usedByExternalApplication: boolean;
};

export function createSavedQuery(self: HttpWrapper, sq: SavedQuerySaveRequest): ResponsePromise<{ id: number; guid: string }> {
    return postCommand(self, 'SavedQueries/Create', sq);
}
export function updateSavedQuery(self: HttpWrapper, id: number, sq: SavedQuerySaveRequest): ResponsePromise<void> {
    return putCommand(self, 'SavedQueries/' + id, sq);
}
export function deleteSavedQuery(self: HttpWrapper, id: number): ResponsePromise<void> {
    return deleteCommand(self, 'SavedQueries/' + id);
}
export function updateSavedQueryUsage(self: HttpWrapper, id: number, usage: SavedQueryUsageUpdateRequest): ResponsePromise<void> {
    return putCommand(self, `SavedQueries/${id}/Usage`, usage);
}
export function getSavedQueries(self: HttpWrapper): Promise<SavedQuery[]> {
    return getResData(self, 'SavedQueries');
}
export function getAvailableQueries(self: HttpWrapper): Promise<SavedQueryHeaderInfo[]> {
    return getResData(self, 'SavedQueries/available');
}
export function getSavedQueryByGuid(self: HttpWrapper, guid: string): Promise<SavedQuery> {
    return getResData(self, `SavedQueries/Guid/${encodeURIComponent(guid)}`);
}
export function getQueryReports(self: HttpWrapper): Promise<SavedQuery[]> {
    return getResData(self, 'SavedQueries/reports');
}

// =============================================================================
// Schema

export function compileDoc(self: HttpWrapper, options: SchemaCompositionOptions): ResponsePromise<Blob> {
    return postCommandPub(self, 'Schema/compile/doc', options, { responseType: 'blob' });
}
export function getCompileCSharp(self: HttpWrapper, identity: string): ResponsePromise<Blob> {
    return getFileCommand(self, `Schema/compile/csharp/${identity}`);
}
export function getAllSchemas(self: HttpWrapper): Promise<SimpleSchema[]> {
    return getResDataPub(self, 'Schema');
}
export function getSchemaDTO(self: HttpWrapper, options: SchemaCompositionOptions): ResponsePromise<SchemaDTO> {
    return postCommandPub(self, 'Schema?noCache=true', options);
}
export function getSchemaMapSchema(self: HttpWrapper): Promise<Code[]> {
    return getResDataPub(self, 'Code/SchemaMapSchema');
}
export function mapMessageWithMultipleSchemas(self: HttpWrapper, form: FormData): ResponsePromise<Blob> {
    return postCommandPub(self, 'Schema/MapMessageWithMultipleSchemas', form);
}
export function getZippedSchemas(self: HttpWrapper, form: FormData): ResponsePromise<Blob> {
    return postCommandPub(self, 'Schema/GetZippedSchemas', form, { responseType: 'blob' });
}

// =============================================================================
// Admin commands

export type CommandResult = {
    success: boolean;
    output: string;
};

export function runAdminCommand(self: HttpWrapper, args: string[]): ResponsePromise<CommandResult> {
    return postCommand(self, 'AdminCommands/', args);
}

// =============================================================================
// RDF

// =============================================================================
// ENS

export type RuleViolationType = 'ERROR' | 'WARNING';

export type EnsFormatRuleViolation = {
    name: string;
    message: string;
    description: string;
    type: RuleViolationType;
};

export type EnsFormatValidationResult = {
    isValid: boolean;
    ruleViolations: EnsFormatRuleViolation[];
    formatName: string;
    syntax: string;
    regex: string;
    regexGrouped: string;
};

export type EnsFormatType = 'TagFormat' | 'DocumentNumberingFormat';

export type ParseMatchElement = {
    description: string;
    referenceType: string;
    value: string;
    valueDescription: string | null;
    referenceLibraryName: string | null;
    isValid: boolean;
};

export type ParseMatch = {
    formatName: string;
    formatDescription: string;
    syntax: string;
    elements: ParseMatchElement[];
};

export type UnmatchedFormat = {
    formatName: string;
    formatDescription: string;
    syntax: string;
};

export type EnsParseResult = {
    formatType: EnsFormatType;
    facility: string;
    category: string;
    number: string;
    matches: ParseMatch[];
    matchFound: boolean;
    unmatchedFormats: UnmatchedFormat[];
};

export function ensValidateFormat(self: HttpWrapper, formatType: EnsFormatType, format: string, axiosOptions?: AxiosRequestConfig): ResponsePromise<EnsFormatValidationResult> {
    return postCommand(self, `Ens/Validate/${formatType}`, format, axiosOptions);
}
export function ensParse(self: HttpWrapper, formatType: EnsFormatType, category: string, facility: string, number: string): Promise<EnsParseResult> {
    return getResDataPub(self, `Ens/Parse/${formatType}` + requestParameters([
        { name: 'category', value: category },
        { name: 'facility', value: facility },
        { name: 'number', value: number }
    ]));
}

// =============================================================================
// Generic views

export type GenericViewQueryParam = NameValuePair;

export type GenericViewQueryParams = GenericViewQueryParam[];

export type GenericViewCompactResult = {
    columns: string[];
    rows: unknown[][];
};

export type InspectSqlResult = {
    sql: string;
};

export type DatabaseInformationSchemaColumn = {
    table_catalog: string;
    table_schema: string;
    table_name: string;
    column_name: string;
    ordinal_position: number;
    column_default: string;
    is_nullable: string;
    data_type: string;
    character_maximum_length: number;
    character_octet_length: number;
    numeric_precision: number;
    numeric_precision_radix: number;
    numeric_scale: number;
    datetime_precision: number;
    character_set_catalog: string;
    character_set_schema: string;
    character_set_name: string;
    collation_catalog: string;
    collation_schema: string;
    collation_name: string;
    domain_catalog: string;
    domain_schema: string;
    domain_name: string;
    // icon, iconTooltip, and typeSuffix added client side after data loading
    icon: string;
    iconTooltip: string;
    typeSuffix: string;
};

export type ViewDefinitionDTO = {
    library: string;
    columns: DatabaseInformationSchemaColumn[];
};

export type QueryParseInfo = {
    query: QuerySpecVersioned;
    text: string;
    sql: string;
};

function queryParameters(ps?: GenericViewQueryParams) {
    if (ps && ps.length > 0) {
        return '?' + ps.map(x => `${x.name.substring(1)}=${encodeURIComponent(x.value)}`).join('&');
    }
    return '';
}

export function genericViewQuery<R>(self: HttpWrapper, q: QuerySpecVersioned): ResponsePromise<R[]> {
    return postCommandPub(self, 'GenericViews/query', q);
}
export function genericViewInspectSql(self: HttpWrapper, querySpec: QuerySpecVersioned): ResponsePromise<InspectSqlResult> {
    return postCommandPub(self, 'GenericViews/query/sql', querySpec);
}
export function genericViewQueryAsTextToExcel(self: HttpWrapper, q: string, p?: GenericViewQueryParams): ResponsePromise<Blob> {
    return postCommandPub(self, 'GenericViews/query/text/excel' + queryParameters(p), q, { responseType: 'blob' });
}

// If value of query parameter is int and has to search in column of type string then it will not work so add quotes to the value
export function genericViewQueryAsText<R>(self: HttpWrapper, q: string, p?: GenericViewQueryParams, options?: AxiosRequestConfig): ResponsePromise<R[]> {
    return postCommandPub(self, 'GenericViews/query/text' + queryParameters(p), q, options);
}
export function genericViewParseText(self: HttpWrapper, q: string): ResponsePromise<QueryParseInfo> {
    return postCommandPub(self, 'GenericViews/query/text/parse', q);
}
export function genericViewSavedQuery<R>(self: HttpWrapper, id: number, p?: GenericViewQueryParams): ResponsePromise<R[]> {
    return postCommandPub(self, `GenericViews/query/saved/${id}` + queryParameters(p));
}
export function genericViewSavedQueryToExcel(self: HttpWrapper, id: number, p?: GenericViewQueryParams): ResponsePromise<Blob> {
    return postCommandPub(self, `GenericViews/query/saved/${id}/excel` + queryParameters(p), null, { responseType: 'blob' });
}
export function genericViewDefinition(self: HttpWrapper, libraryName: string): Promise<ViewDefinitionDTO> {
    return getResDataPub(self, `GenericViews/library/${encodeURIComponent(libraryName)}/definition?noCache=true`);
}

// =============================================================================
// SQL query

export type SqlQueryRequest = {
    query: string;
    arguments?: Record<string, string>;
};

export function sqlQuery(self: HttpWrapper, q: SqlQueryRequest): ResponsePromise<Record<string, unknown>[]> {
    return postCommandPub(self, 'sql', q);
}
export function sqlQueryToExcel(self: HttpWrapper, q: SqlQueryRequest): ResponsePromise<Blob> {
    return postCommandPub(self, 'sql/excel', q, { responseType: 'blob' });
}

// =============================================================================
// Metadata

export type MetadataRequest = {
    request: string;
    values: string[];
};

export type MetadataResponse = {
    'access-groups-for-library': Record<string, AccessGroup[]>;
    'code-sets': Record<string, CodeSet[]>;
    'attribute-definitions': Record<string, Library>;
};

export function getMetadata(self: HttpWrapper, array: MetadataRequest[]): ResponsePromise<MetadataResponse> {
    return postCommand(self, 'Metadata', array);
}

// =============================================================================
// Attachment

export type SearchAttachmentsParameters = {
    libraryName: string;
    mimeType: string;
    fileNameLike: string;
};

export type SearchAttachmentResult = {
    libraryName: string;
    codeSetDescription: string;
    codeSetName: string;
    codeId?: number;
    codeName: string;
    attachmentKey: string;
    mimeType: string;
    fileName: string;
    sizeInBytes: number;
    attachmentUpdatedDate: string;
    storageLocation: string;
    libraryGroup: string;
};

export function getAttachment(self: HttpWrapper, key: string): Promise<Blob> {
    return getResDataPub(self, `Attachment/File/${key}`, { responseType: 'blob' });
}
export function getAttachmentMetadata(self: HttpWrapper, attachmentKey: string): Promise<Attachment> {
    return getResData(self, `Attachment/Metadata/${attachmentKey}`);
}
export function getAttachmentName(self: HttpWrapper, key: string): Promise<string> {
    return getResDataPub(self, `Attachment/FileName/${key}`);
}
export function getMimeTypes(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'Attachment/MimeTypes');
}
export function uploadAttachmentToCode(self: HttpWrapper, codeId: number, form: FormData): ResponsePromise<{ attachmentKey: string }> {
    return postCommand(self, `Attachment/SaveToCode/${codeId}`, form);
}
export function uploadAttachmentToLibrary(self: HttpWrapper, libraryName: string, form: FormData): ResponsePromise<{ attachmentKey: string }> {
    return postCommand(self, `Attachment/SaveToLibrary/${encodeURIComponent(libraryName)}`, form);
}
export function uploadAttachmentToTag(self: HttpWrapper, tagName: string, form: FormData): ResponsePromise<{ attachmentKey: string }> {
    return postCommand(self, `Attachment/SaveToTag/${encodeURIComponent(tagName)}`, form);
}
export function searchAttachments(self: HttpWrapper, params: SearchAttachmentsParameters): ResponsePromise<SearchAttachmentResult> {
    return postCommand(self, 'Attachment/Search', params);
}

export function getLibraryNames(self: HttpWrapper): Promise<string[]> {
    return getResDataPub(self, 'Library/NameList');
}

// =============================================================================
// Excel

export type CodeFilter = {
    regEx: string[];
    queries: string[][];
    columns: string[];
};

export function excelImportCodeSet(self: HttpWrapper, form: FormData): ResponsePromise<{ releaseId: number }> {
    return postCommand(self, 'Excel/ImportCodeSet', form);
}
export function excelExportCodeSet(self: HttpWrapper, codeSetName: string, codeFilter: CodeFilter): ResponsePromise<Blob> {
    return postCommand(self, 'Excel/ExportCodeSet', codeFilter, {
        params: { name: codeSetName },
        responseType: 'blob'
    });
}

// =============================================================================
// Access Policy

export type LibraryPolicy = {
    id: number;
    libraryName: string;
    policyId: number;
    role: string;
    system: string;
};

export type LibraryPolicyDto = {
    id: number | null;
    libraryName: string;
    facilityRole: string;
    sourceSystem: string;
};

export type Policy = {
    id: number;
    sourceSystem: string;
    facilityRole: string;
    scope?: string;
    commonLibrary?: boolean;
    dateTimeExpire: string;
    extend?: boolean;
};

export type PolicyDto = {
    id: number;
    libraryName: string;
    policies: Policy[];
};

export type UserAttributesDto = {
    id: number;
    email: string;
    fullName?: string;
    policies: Policy[];
};

export type ApplicationAttributeDto = {
    id: number | null;
    objectId: string;
    applicationName: string;
    applicationIri: string;
    role: string;
    system: string;
    scope: string;
};

export type AttributeDto = {
    id: number | null;
    email: string | null;
    facilityRole: string;
    sourceSystem: string;
    scope: string;
    commonLibrary: boolean;
    extend?: boolean;
};

export type PolicyRole = {
    name: string;
    externalName: string;
    description: string;
    systemName: string;
};

export type PolicySystem = {
    name: string;
    description: string;
    roles: PolicyRole[];
};

export type UserAccessAttribute = {
    id: number;
    oid: string;
    email: string;
    facility: string;
    sourceSystem: string;
    facilityRole: string;
    dateTimeChecked: string;
    dateTimeExpire: string;
    commonLibrary: boolean;
};

/**
 * Fetches all the users attributes
 */
export function getMyUserAccessAttributes(self: HttpWrapper): Promise<string[]> {
    return getResDataOrEmpty(self, 'Me/attributes');
}
export function getAllAccessPolicies(self: HttpWrapper): Promise<PolicyDto[]> {
    return getResDataOrEmpty(self, 'AccessPolicy');
}
export function getAllSystemsAndRoles(self: HttpWrapper): Promise<PolicySystem[]> {
    return getResDataOrEmpty(self, 'AccessPolicy/Systems');
}
export function getAllUserAttributes(self: HttpWrapper): Promise<UserAttributesDto[]> {
    return getResDataOrEmpty(self, 'AccessPolicy/UserAttributes');
}
export async function getAccessPolicesForLib(self: HttpWrapper, libraryName: string): Promise<Policy[]> {
    const res: PolicyDto | [] = await getResDataOrEmpty(self, `AccessPolicy/${encodeURIComponent(libraryName)}`);
    return Array.isArray(res) ? res : res.policies;
}
export function saveLibraryPolicy(self: HttpWrapper, libraryName: string, policy: LibraryPolicyDto): Promise<LibraryPolicyDto> {
    return postResData(self, `AccessPolicy/${encodeURIComponent(libraryName)}`, policy);
}
export function saveUserAttribute(self: HttpWrapper, email: string, attribute: AttributeDto): Promise<UserAccessAttribute> {
    return postResData(self, `AccessPolicy/UserAttributes/${encodeURIComponent(email)}`, attribute);
}
export function deleteLibraryPolicy(self: HttpWrapper, libraryName: string, policy: LibraryPolicyDto): ResponsePromise<void> {
    return deleteCommand(self, `AccessPolicy/${encodeURIComponent(libraryName)}`, policy);
}
export function expireUserAttribute(self: HttpWrapper, email: string, attribute: AttributeDto): ResponsePromise<void> {
    return putCommand(self, `AccessPolicy/UserAttributes/${encodeURIComponent(email)}`, attribute);
}
export function restoreExpiredUserAttribute(self: HttpWrapper, email: string, attribute: AttributeDto): ResponsePromise<void> {
    return putCommand(self, `AccessPolicy/UserAttributes/${encodeURIComponent(email)}`, attribute);
}
export function savePolicySystems(self: HttpWrapper, policySystem: PolicySystem): Promise<void> {
    return postResData(self, `AccessPolicy/Systems`, policySystem);
}
export function updatePolicySystems(self: HttpWrapper, policySystem: PolicySystem): ResponsePromise<void> {
    return putCommand(self, `AccessPolicy/Systems/${policySystem.name}`, policySystem);
}
export function deletePolicySystems(self: HttpWrapper, policySystem: PolicySystem): ResponsePromise<void> {
    return deleteCommand(self, `AccessPolicy/Systems/${policySystem.name}`, policySystem);
}
export function savePolicyRoles(self: HttpWrapper, policyRole: PolicyRole): Promise<void> {
    return postResData(self, `AccessPolicy/Systems/${policyRole.systemName}/Roles/`, policyRole);
}
export function deletePolicyRoles(self: HttpWrapper, policyRole: PolicyRole): ResponsePromise<void> {
    return deleteCommand(self, `AccessPolicy/Systems/${policyRole.systemName}/Roles/${policyRole.name}`, policyRole);
}
export function getAllApplicationAttributes(self: HttpWrapper): Promise<ApplicationAttributeDto[]> {
    return getResDataOrEmpty(self, 'AccessPolicy/ApplicationAttributes');
}
export function saveApplicationAttribute(self: HttpWrapper, objectId: string, attribute: AttributeDto): Promise<ApplicationAttributeDto> {
    return postResData(self, `AccessPolicy/ApplicationAttributes/${objectId}`, attribute);
}
export function deleteApplicationAttribute(self: HttpWrapper, attribute: ApplicationAttributeDto): ResponsePromise<void> {
    return deleteCommand(self, `AccessPolicy/ApplicationAttributes/${attribute.objectId}/${attribute.id}`);
}

// =============================================================================
// Internal Application

export type CreateApplicationModel = {
    name: string;
};

export type CreateSubscriptionModel = {
    libraryName: string;
    applicationName: string;
};

export function getInternalApplication(self: HttpWrapper, appName: string): Promise<Application> {
    return getResData(self, 'Application/' + encodeURIComponent(appName));
}
export function getInternalApplicationSubscriptions(self: HttpWrapper, appName: string): Promise<Subscription[]> {
    return getResData(self, 'Application/Subscription?appName=' + encodeURIComponent(appName));
}
export function getAllInternalApplication(self: HttpWrapper): Promise<Application[]> {
    return getResData(self, 'Application/All');
}
export function saveNewInternalApplicationSubscription(self: HttpWrapper, command: CreateSubscriptionModel): ResponsePromise<void> {
    return postCommand(self, 'Application/Subscription', command);
}
export function disableSubscriptionForApplication(self: HttpWrapper, subscriptionId: number): ResponsePromise<void> {
    return postCommand(self, `Application/Subscription/Disable/${subscriptionId}`);
}
export function enableSubscriptionForApplication(self: HttpWrapper, subscriptionId: number): ResponsePromise<void> {
    return postCommand(self, `Application/Subscription/Enable/${subscriptionId}`);
}

export function getCodeSetSubscriptions(self: HttpWrapper, codeSetName: string): Promise<CodeSetSubscriptions> {
    return getResData(self, `Application/Subscription/CodeSet/${encodeURIComponent(codeSetName)}`);
}

// =============================================================================
// System Information

export type ServiceInfo = {
    environment: string;
    machineName: string;
    os: string;
    settings: { path: string; key: string; value: string }[];
    resourceGroup: string;
    apiVersion: string;
};

export function getSystemInfo(self: HttpWrapper): Promise<ServiceInfo> {
    return getResData(self, 'ServiceInfo');
}

// =============================================================================
// Disabled Feature

export function disabledFeaturesGetAll(self: HttpWrapper): Promise<DisabledFeature[]> {
    return getResData(self, 'DisabledFeature/All');
}

export function disabledFeaturesGetDisabled(self: HttpWrapper): Promise<DisabledFeature[]> {
    return getResData(self, 'DisabledFeature/Disabled');
}

export function disabledFeaturesDelete(self: HttpWrapper, id: number): ResponsePromise<void> {
    return deleteCommand(self, `DisabledFeature/${id}`);
}

export function disableFeaturesSave(self: HttpWrapper, feature: DisabledFeature): ResponsePromise<void> {
    return postCommand(self, 'DisabledFeature', feature);
}

// =============================================================================
// Governance and Documentation

export type InformationElementDto = {
    name: string;
    value: string;
};

export type TagDto = {
    name: string;
    attachmentKey?: string | null;
    fileName?: string | null;
    elements: InformationElementDto[];
};

export type LibraryDocumentationDto = {
    libraryName: string;
    attachmentKey?: string | null;
    fileName?: string | null;
    elements: InformationElementDto[];
    tags: TagDto[];
};

export function getLibraryDocumentation(self: HttpWrapper, libraryName: string): Promise<LibraryDocumentationDto> {
    return getResData(self, `LibraryDocumentation/${encodeURIComponent(libraryName)}`);
}

// =============================================================================
// Library Information Element
export function getLibraryInformationElement(self: HttpWrapper, libraryName: string): Promise<Record<string, string>> {
    return getResData(self, `LibraryInformationElement/${encodeURIComponent(libraryName)}`);
}

export function getLibraryInformationElementUniqueKeys(self: HttpWrapper): Promise<string[]> {
    return getResData(self, 'LibraryInformationElement/UniqueKeys');
}

export function saveLibraryInformationElement(self: HttpWrapper, libraryName: string, elements: string): ResponsePromise<void> {
    return postCommand(self, `LibraryInformationElement/${encodeURIComponent(libraryName)}`, elements);
}

export function deleteLibraryInformationElement(self: HttpWrapper, libraryName: string, element: string): ResponsePromise<void> {
    return deleteCommand(self, `LibraryInformationElement/${encodeURIComponent(libraryName)}/${encodeURIComponent(element)}`);
}

// =============================================================================
// Shipping

export type ShippingPackageChange = {
    id: number;
    sequenceNumber: number;
    isApplied: boolean;
    isFailure: boolean;
    isAuthorized: boolean;
    changeType: string;
    description: string;
    data: string;
};

export type ShippingPackage = {
    id: number;
    packageId: string;
    ownerId: string;
    status: string;
    packageType: string;
    dateCreated: string;
    dateUpdated: string;
    name: string | null;
    description: string;
    remarks: string | null;
    extractionOptions: string;
    extractedFromSystem: string;
    extractedByUserId: string;
    extractedByUserPrincipalName: string;
    dateExtracted: string;
    uploadedByUserId: string | null;
    dateUploaded: string | null;
    dateImported: string | null;
    log: object[] | null;
    changes: ShippingPackageChange[] | null;
};

export type ShippingCreatePackageResponse = {
    packageId: string;
};

export type ShippingUploadPackageResponse = {
    packageId: string;
};

export function getShippingPackages(self: HttpWrapper): Promise<ShippingPackage[]> {
    return getResData(self, 'Shipping');
}

export function getShippingPackage(self: HttpWrapper, packageId: string): Promise<ShippingPackage> {
    return getResData(self, `Shipping/Package/${packageId}`);
}

export function downloadShippingPackage(self: HttpWrapper, packageId: string): ResponsePromise<Blob> {
    return getInternalFileCommand(self, `Shipping/Download/${packageId}`);
}

export function createShippingPackage(self: HttpWrapper,
    packageType: string,
    name: string,
    description: string,
    remarks: string,
    options: object): ResponsePromise<ShippingCreatePackageResponse> {
    return postCommand(self, 'Shipping/CreatePackage', {
        packageType,
        name,
        description,
        remarks,
        extractionOptionsJson: JSON.stringify(options)
    });
}

export function updateShippingPackage(self: HttpWrapper,
    packageId: string,
    description: string,
    options: object): ResponsePromise<void> {
    return postCommand(self, `Shipping/Package/${packageId}/Edit`, {
        description,
        extractionOptionsJson: JSON.stringify(options)
    });
}

export function shippingPackageSetName(self: HttpWrapper, packageId: string, name: string): ResponsePromise<void> {
    return postCommand(self, `Shipping/Package/${packageId}/Name`, { name });
}

export function shippingPackageSetRemarks(self: HttpWrapper, packageId: string, remarks: string): ResponsePromise<void> {
    return postCommand(self, `Shipping/Package/${packageId}/Remarks`, { remarks });
}

export function uploadShippingPackage(self: HttpWrapper, form: FormData): ResponsePromise<ShippingUploadPackageResponse> {
    return postCommand(self, 'Shipping/Upload', form);
}

export function deleteShippingPackage(self: HttpWrapper, packageId: string): ResponsePromise<void> {
    return deleteCommand(self, `Shipping/Package/${packageId}`);
}

export function importShippingPackage(self: HttpWrapper, packageId: string): ResponsePromise<void> {
    return postCommand(self, `Shipping/Import/${packageId}`);
}

// =============================================================================
// TR Document

export type TRDocumentContentLibrary = {
    name: string;
    codeSetName: string;
    attributeName: string;
    contentForLibrary: string;
    contentForCodeSet: string;
    trReferenceAttributeName: string;
    trNotesAttributeName: string;
};

export type NewTrRevisionRequest = {
    name: string;
    description: string;
};

export type CreateTrSourceItemRequest = {
    reasonToAdd: string;
    codeSetName: string;
    code: BatchDocumentCode;
};

export type CreateTrSourceItemResponse = {
    releaseId: number;
    sourceCodeIdentity: string;
    contentCodeIdentity: string;
};

export function getTRDocumentContentLibraries(self: HttpWrapper, trDocumentIdentity: string): Promise<TRDocumentContentLibrary[]> {
    return getResData(self, `TRDocument/${trDocumentIdentity}/ContentLibraries`);
}

export async function trDocumentMakeLinksExcel(self: HttpWrapper, revisionIdentity: string): Promise<Blob> {
    const responseData = await getResData(
        self,
        `TRDocument/Revision/${encodeURIComponent(revisionIdentity)}/Links/Excel`,
        { responseType: 'blob' });
    return new Blob([responseData], {
        type: responseData.type
    });
}

// Returns the id of the created release
export function finalizeTrRevision(self: HttpWrapper, trRevisionIdentity: string): ResponsePromise<number> {
    return postCommand(self, `TRDocument/Revision/${encodeURIComponent(trRevisionIdentity)}/Finalize`);
}

export function createNewTrDocumentRevision(self: HttpWrapper, trDocumentIdentity: string, newTrRevision: NewTrRevisionRequest): ResponsePromise<number> {
    return postCommand(self, `TRDocument/${encodeURIComponent(trDocumentIdentity)}/Revision/New`, newTrRevision);
}

export function createNewTrContentItem(self: HttpWrapper, trRevisionIdentity: string, request: CreateTrSourceItemRequest): ResponsePromise<CreateTrSourceItemResponse> {
    return postCommand(self, `TRDocument/Revision/${encodeURIComponent(trRevisionIdentity)}/AddSourceItem`, request);
}
// =============================================================================
// Status
export enum Status {
    Normal = 0,
    CommonLibraryReadonly = 1,
    StidUnavailable = 2,
    TieUnavailable = 3
}

export type OperationalStatusMessage = {
    status: Status;
    title: string | null;
    message: string | null;
    displayMessageFrom: string | null;
    from: string | null;
    to: string | null;
};

export type StatusResponse = {
    operationalStatuses: OperationalStatusMessage[];
};

export function getStatus(self: HttpWrapper): Promise<StatusResponse> {
    return getResData(self, 'Status');
}

// =============================================================================
// Frontpage

export type FrontpageItem = {
    icon: string;
    label: string;
    href: string;
};

export type FrontpageItemGroup = {
    label: string;
    class: string;
    items: FrontpageItem[];
};

export function getFrontpageItems(self: HttpWrapper): Promise<FrontpageItemGroup[]> {
    return getResData(self, 'Frontpage');
}

// =============================================================================
// Regex Validation

export type RegexValidationResult = {
    isValid: boolean;
    isMatch: boolean;
    error?: string;
};

export async function validateRegexOnServer(self: HttpWrapper, pattern: string, value?: string): Promise<RegexValidationResult> {
    const url = constructInternalUrl('RegexValidation/validate');
    const response = await self.$http.post<RegexValidationResult>(url, { pattern, value });
    return response.data;
}
